import { makeStyles, tokens, Spinner, Button } from '@coreai/fluentui-react';
import { FilterDismissRegular, DismissRegular, HistoryRegular } from '@fluentui/react-icons';
import { TriggerHistoryItem } from './TriggerHistoryItem';
import { MonitoringEmptyState } from '@/components/MonitoringView/shared/MonitoringEmptyState';
import type { TriggerHistory } from '@/types/monitoring';

type TriggerHistoryListProps = {
  histories: TriggerHistory[];
  isLoading: boolean;
  selectedHistoryName: string | null;
  onSelect: (historyName: string) => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
  hasActiveFilters?: boolean;
  onClearFilters?: () => void;
};

const useStyles = makeStyles({
  container: { flex: 1, overflow: 'auto', minHeight: 0 },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: tokens.spacingVerticalXXL,
  },
  list: { display: 'flex', flexDirection: 'column' },
  loadMore: {
    display: 'flex',
    justifyContent: 'center',
    padding: tokens.spacingVerticalM,
  },
});

export const TriggerHistoryList = ({
  histories,
  isLoading,
  selectedHistoryName,
  onSelect,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  hasActiveFilters = false,
  onClearFilters,
}: TriggerHistoryListProps) => {
  const styles = useStyles();

  if (isLoading && histories.length === 0) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="small" label="Loading trigger history..." />
      </div>
    );
  }

  if (histories.length === 0) {
    return hasActiveFilters ? (
      <MonitoringEmptyState
        icon={<FilterDismissRegular />}
        title="No matching trigger evaluations"
        subtitle="No trigger evaluations match your filters. Try adjusting or clearing them."
        actions={
          onClearFilters ? (
            <Button
              appearance="primary"
              size="small"
              icon={<DismissRegular />}
              onClick={onClearFilters}
            >
              Clear filters
            </Button>
          ) : undefined
        }
      />
    ) : (
      <MonitoringEmptyState
        icon={<HistoryRegular />}
        title="No trigger evaluations yet"
        subtitle="Trigger evaluations will appear here once this trigger fires."
      />
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.list} role="listbox" aria-label="Trigger history">
        {histories.map((h) => (
          <TriggerHistoryItem
            key={h.id}
            history={h}
            isSelected={h.name === selectedHistoryName}
            onSelect={onSelect}
          />
        ))}
      </div>
      {hasNextPage && (
        <div className={styles.loadMore}>
          <Button
            appearance="subtle"
            size="small"
            onClick={onLoadMore}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage ? 'Loading…' : 'Load more'}
          </Button>
        </div>
      )}
    </div>
  );
};
